import api from './client';

export interface ProxyStats {
  total: number;
  successes: number;
  failures: number;
  avg_duration_ms: number;
  by_operation: {
    operation: string;
    total: number;
    successes: number;
    failures: number;
    success_rate: number;
    avg_duration_ms: number;
  }[];
}

export interface ProxyError {
  timestamp: string;
  operation: string;
  error: string;
}

export interface ProxyLogEntry {
  ts: string;
  level: string;
  step: string;
  message: string;
}

export interface ProxyOperation {
  timestamp: string;
  operation: string;
  username: string;
  operator_email: string;
  success: boolean;
  error: string;
  duration_ms: number;
  // Persisted ops (from /admin/proxies/{org_id}/operations) also include these.
  // Live in-memory ops on org.servicebox.proxy_recent_operations don't.
  id?: string;
  ordre_id?: string;
  dossier_id?: string;
  request_data?: Record<string, unknown>;
}

export interface ProxyOperationsPage {
  items: ProxyOperation[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ProxyOperationsFilters {
  ordre_id?: string;
  dossier_id?: string;
  operation?: string;
  success?: boolean;
  limit?: number;
  cursor_timestamp?: string;
}

export interface ProxySystemInfo {
  hostname: string;
  platform?: string;
  uptime_seconds?: number;
}

export interface ProxyOutage {
  start_at: string;
  end_at: string | null; // null while the outage is still ongoing
  duration_min: number;
  ongoing?: boolean;
}

export interface ProxyInfo {
  org_id: string;
  org_name: string;
  proxy_url: string | null;
  proxy_version: string;
  proxy_last_seen: string | null;
  proxy_last_outage_start_at?: string | null;
  proxy_last_outage_end_at?: string | null;
  proxy_last_outage_duration_min?: number | null;
  proxy_outages_30d?: ProxyOutage[];
  proxy_stats: ProxyStats | null;
  proxy_recent_errors: ProxyError[];
  proxy_recent_logs: ProxyLogEntry[];
  proxy_recent_operations: ProxyOperation[];
  proxy_system_info: ProxySystemInfo | null;
  servicebox_enabled: boolean;
  servicebox_username: string;
  pending_commands: { action: string; queued_at: string }[];
}

export interface LatestVersion {
  version: string | null;
  tag?: string;
  published_at?: string;
}

export const proxyAdminApi = {
  list: async (): Promise<ProxyInfo[]> => {
    const { data } = await api.get('/admin/proxies');
    return data;
  },

  sendCommand: async (orgId: string, action: string) => {
    const { data } = await api.post(`/admin/proxies/${orgId}/command`, { action });
    return data;
  },

  getLatestVersion: async (): Promise<LatestVersion> => {
    const { data } = await api.get('/admin/proxies/latest-version');
    return data;
  },

  listOperations: async (orgId: string, filters: ProxyOperationsFilters = {}): Promise<ProxyOperationsPage> => {
    const params: Record<string, string | number | boolean> = {};
    if (filters.ordre_id) params.ordre_id = filters.ordre_id;
    if (filters.dossier_id) params.dossier_id = filters.dossier_id;
    if (filters.operation) params.operation = filters.operation;
    if (filters.success !== undefined) params.success = filters.success;
    if (filters.limit) params.limit = filters.limit;
    if (filters.cursor_timestamp) params.cursor_timestamp = filters.cursor_timestamp;
    const { data } = await api.get(`/admin/proxies/${orgId}/operations`, { params });
    return data;
  },
};
